/**
 * Recipe Suggestion Agent
 * Provides recipe suggestions based on available produce and user preferences
 */

import { recipes as loadedRecipes } from '../../data/content';

interface Recipe {
  id: string;
  title?: string;
  name?: string;
  description: string;
  ingredients: any[];
  primaryProduce: string[];
  prepTime: number | string; // minutes or string like "10 minutes"
  cookTime: number | string; // minutes or string like "15 minutes"
  servings: number;
  difficulty: 'easy' | 'medium' | 'hard';
  cuisine?: string;
  dietaryTags: string[];
  instructions: string[];
  nutritionInfo?: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
}

interface RecipeFilter {
  ingredients?: string[];
  maxPrepTime?: number;
  maxCookTime?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  dietaryRestrictions?: string[];
  cuisine?: string;
}

class RecipeAgent {
  private recipes: Recipe[];
  private hardcodedRecipes: Recipe[];

  constructor() {
    this.hardcodedRecipes = this.initializeHardcodedRecipes();
    this.recipes = this.loadAllRecipes();
  }

  /**
   * Load all recipes from content and merge with hardcoded
   */
  private loadAllRecipes(): Recipe[] {
    const normalized = loadedRecipes.map(recipe => this.normalizeRecipe(recipe));
    return [...normalized, ...this.hardcodedRecipes];
  }

  /**
   * Normalize recipe data from JSON to match interface
   */
  private normalizeRecipe(recipe: any): Recipe {
    const parseTime = (time: any): number => {
      if (typeof time === 'number') return time;
      if (typeof time === 'string') {
        const match = time.match(/(\d+)/);
        return match ? parseInt(match[1]) : 0;
      }
      return 0;
    };

    const normalizedIngredients = Array.isArray(recipe.ingredients)
      ? recipe.ingredients.map((ing: any) =>
          typeof ing === 'string' ? ing : (ing.name || '')
        )
      : [];

    return {
      id: recipe.id,
      name: recipe.title || recipe.name || '',
      description: recipe.description || '',
      ingredients: normalizedIngredients,
      primaryProduce: Array.isArray(recipe.primaryProduce) ? recipe.primaryProduce : [],
      prepTime: parseTime(recipe.prepTime),
      cookTime: parseTime(recipe.cookTime),
      servings: recipe.servings || 4,
      difficulty: recipe.difficulty || 'easy',
      cuisine: recipe.cuisine || 'American',
      dietaryTags: Array.isArray(recipe.dietaryTags) ? recipe.dietaryTags : [],
      instructions: Array.isArray(recipe.instructions) ? recipe.instructions : [],
      nutritionInfo: recipe.nutritionInfo,
    };
  }

  /**
   * Initialize hardcoded recipe database (fallback/examples)
   */
  private initializeHardcodedRecipes(): Recipe[] {
    return [
      {
        id: 'tomato-basil-salad',
        name: 'Fresh Tomato Basil Salad',
        description: 'A simple and refreshing salad showcasing fresh tomatoes',
        ingredients: ['tomatoes', 'fresh basil', 'mozzarella', 'olive oil', 'balsamic vinegar'],
        primaryProduce: ['tomatoes', 'basil'],
        prepTime: 10,
        cookTime: 0,
        servings: 4,
        difficulty: 'easy',
        cuisine: 'Italian',
        dietaryTags: ['vegetarian', 'gluten-free'],
        instructions: [
          'Slice fresh tomatoes',
          'Tear fresh basil leaves',
          'Slice mozzarella',
          'Arrange on a plate',
          'Drizzle with olive oil and balsamic vinegar',
          'Season with salt and pepper',
        ],
        nutritionInfo: {
          calories: 180,
          protein: 8,
          carbs: 12,
          fat: 12,
        },
      },
      {
        id: 'strawberry-smoothie',
        name: 'Florida Strawberry Smoothie',
        description: 'A refreshing smoothie with local strawberries',
        ingredients: ['strawberries', 'banana', 'yogurt', 'honey', 'ice'],
        primaryProduce: ['strawberries'],
        prepTime: 5,
        cookTime: 0,
        servings: 2,
        difficulty: 'easy',
        cuisine: 'American',
        dietaryTags: ['vegetarian', 'gluten-free'],
        instructions: [
          'Wash and hull strawberries',
          'Add all ingredients to blender',
          'Blend until smooth',
          'Pour into glasses and serve',
        ],
        nutritionInfo: {
          calories: 150,
          protein: 6,
          carbs: 28,
          fat: 2,
        },
      },
      {
        id: 'grilled-peppers',
        name: 'Grilled Bell Peppers with Herbs',
        description: 'Colorful peppers grilled to perfection',
        ingredients: ['bell peppers', 'olive oil', 'garlic', 'fresh herbs', 'lemon'],
        primaryProduce: ['peppers', 'herbs'],
        prepTime: 10,
        cookTime: 15,
        servings: 4,
        difficulty: 'easy',
        cuisine: 'Mediterranean',
        dietaryTags: ['vegan', 'gluten-free', 'paleo'],
        instructions: [
          'Cut peppers into quarters',
          'Brush with olive oil',
          'Grill for 15 minutes, turning occasionally',
          'Toss with minced garlic and fresh herbs',
          'Squeeze lemon over top',
        ],
        nutritionInfo: {
          calories: 120,
          protein: 2,
          carbs: 15,
          fat: 7,
        },
      },
      {
        id: 'avocado-toast',
        name: 'Florida Avocado Toast',
        description: 'Classic avocado toast with a Florida twist',
        ingredients: ['avocado', 'bread', 'lemon', 'salt', 'red pepper flakes', 'tomatoes'],
        primaryProduce: ['avocados', 'tomatoes'],
        prepTime: 5,
        cookTime: 3,
        servings: 2,
        difficulty: 'easy',
        cuisine: 'American',
        dietaryTags: ['vegetarian', 'vegan'],
        instructions: [
          'Toast bread',
          'Mash avocado with lemon juice',
          'Spread on toast',
          'Top with sliced tomatoes',
          'Season with salt and red pepper flakes',
        ],
        nutritionInfo: {
          calories: 280,
          protein: 8,
          carbs: 32,
          fat: 15,
        },
      },
      {
        id: 'cucumber-salad',
        name: 'Refreshing Cucumber Salad',
        description: 'Light and crispy cucumber salad',
        ingredients: ['cucumbers', 'red onion', 'dill', 'vinegar', 'olive oil'],
        primaryProduce: ['cucumbers'],
        prepTime: 15,
        cookTime: 0,
        servings: 4,
        difficulty: 'easy',
        cuisine: 'Mediterranean',
        dietaryTags: ['vegan', 'gluten-free', 'low-carb'],
        instructions: [
          'Slice cucumbers thinly',
          'Thinly slice red onion',
          'Chop fresh dill',
          'Mix vinegar and olive oil',
          'Toss everything together',
          'Refrigerate for 30 minutes before serving',
        ],
        nutritionInfo: {
          calories: 80,
          protein: 1,
          carbs: 8,
          fat: 5,
        },
      },
      {
        id: 'citrus-salad',
        name: 'Florida Citrus Salad',
        description: 'A vibrant salad featuring local citrus',
        ingredients: ['oranges', 'grapefruit', 'lettuce', 'avocado', 'honey', 'olive oil'],
        primaryProduce: ['citrus', 'lettuce', 'avocados'],
        prepTime: 15,
        cookTime: 0,
        servings: 4,
        difficulty: 'easy',
        cuisine: 'American',
        dietaryTags: ['vegetarian', 'gluten-free', 'paleo'],
        instructions: [
          'Segment citrus fruits',
          'Tear lettuce into pieces',
          'Slice avocado',
          'Arrange on plates',
          'Whisk honey and olive oil for dressing',
          'Drizzle over salad',
        ],
        nutritionInfo: {
          calories: 200,
          protein: 3,
          carbs: 25,
          fat: 11,
        },
      },
      {
        id: 'sauteed-greens',
        name: 'Sautéed Leafy Greens with Garlic',
        description: 'Simple and nutritious greens',
        ingredients: ['leafy greens', 'garlic', 'olive oil', 'lemon', 'salt'],
        primaryProduce: ['greens'],
        prepTime: 5,
        cookTime: 10,
        servings: 4,
        difficulty: 'easy',
        cuisine: 'Mediterranean',
        dietaryTags: ['vegan', 'gluten-free', 'paleo', 'low-carb'],
        instructions: [
          'Wash and chop greens',
          'Heat olive oil in pan',
          'Add minced garlic',
          'Add greens and sauté until wilted',
          'Squeeze lemon over top',
          'Season with salt',
        ],
        nutritionInfo: {
          calories: 90,
          protein: 3,
          carbs: 8,
          fat: 6,
        },
      },
    ];
  }

  /**
   * Get recipe suggestions based on available ingredients
   */
  getRecipesByIngredients(ingredients: string[], limit: number = 10): Recipe[] {
    const lowerIngredients = ingredients.map((i) => i.toLowerCase());

    const scored = this.recipes.map((recipe) => {
      const matchCount = recipe.primaryProduce.filter((produce) =>
        lowerIngredients.some((ing) => produce.toLowerCase().includes(ing))
      ).length;

      return { recipe, score: matchCount };
    });

    return scored
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map((s) => s.recipe);
  }

  /**
   * Filter recipes by criteria
   */
  filterRecipes(filter: RecipeFilter): Recipe[] {
    return this.recipes.filter((recipe) => {
      // Check ingredients
      if (filter.ingredients && filter.ingredients.length > 0) {
        const hasIngredient = filter.ingredients.some((ing) =>
          recipe.primaryProduce.some((p) => p.toLowerCase().includes(ing.toLowerCase()))
        );
        if (!hasIngredient) return false;
      }

      // Check prep time
      if (filter.maxPrepTime && recipe.prepTime > filter.maxPrepTime) {
        return false;
      }

      // Check cook time
      if (filter.maxCookTime && recipe.cookTime > filter.maxCookTime) {
        return false;
      }

      // Check difficulty
      if (filter.difficulty && recipe.difficulty !== filter.difficulty) {
        return false;
      }

      // Check dietary restrictions
      if (filter.dietaryRestrictions && filter.dietaryRestrictions.length > 0) {
        const meetsRestrictions = filter.dietaryRestrictions.every((restriction) =>
          recipe.dietaryTags.includes(restriction)
        );
        if (!meetsRestrictions) return false;
      }

      // Check cuisine
      if (filter.cuisine && recipe.cuisine !== filter.cuisine) {
        return false;
      }

      return true;
    });
  }

  /**
   * Get quick recipes (under 20 minutes total time)
   */
  getQuickRecipes(): Recipe[] {
    return this.recipes.filter((recipe) => {
      const prepTime = typeof recipe.prepTime === 'number' ? recipe.prepTime : parseInt(String(recipe.prepTime)) || 0;
      const cookTime = typeof recipe.cookTime === 'number' ? recipe.cookTime : parseInt(String(recipe.cookTime)) || 0;
      return prepTime + cookTime <= 20;
    });
  }

  /**
   * Get recipes by difficulty
   */
  getRecipesByDifficulty(difficulty: 'easy' | 'medium' | 'hard'): Recipe[] {
    return this.recipes.filter((recipe) => recipe.difficulty === difficulty);
  }

  /**
   * Get recipes by dietary tag
   */
  getRecipesByDietaryTag(tag: string): Recipe[] {
    return this.recipes.filter((recipe) => recipe.dietaryTags.includes(tag));
  }

  /**
   * Search recipes by name or description
   */
  searchRecipes(query: string): Recipe[] {
    const lowerQuery = query.toLowerCase();
    return this.recipes.filter(
      (recipe) =>
        (recipe.name || '').toLowerCase().includes(lowerQuery) ||
        recipe.description.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Get seasonal recipe suggestions
   */
  getSeasonalRecipes(seasonalProduce: string[]): Recipe[] {
    return this.getRecipesByIngredients(seasonalProduce);
  }

  /**
   * Get recipe by ID
   */
  getRecipeById(id: string): Recipe | null {
    return this.recipes.find((recipe) => recipe.id === id) || null;
  }

  /**
   * Get all recipes
   */
  getAllRecipes(): Recipe[] {
    return this.recipes;
  }

  /**
   * Get recipe recommendations based on time of day
   */
  getRecipesByMealTime(mealTime: 'breakfast' | 'lunch' | 'dinner' | 'snack'): Recipe[] {
    // This is a simple implementation - can be expanded
    if (mealTime === 'breakfast') {
      return this.recipes.filter(
        (r) =>
          (r.name || '').toLowerCase().includes('smoothie') ||
          (r.name || '').toLowerCase().includes('toast')
      );
    } else if (mealTime === 'snack') {
      return this.getQuickRecipes();
    }
    return this.recipes;
  }

  /**
   * Get recipes that use a specific ingredient
   * 🌟 CRITICAL for "Basil Example" - Recipe → Basil bidirectional linking
   * @param ingredient - The ingredient to search for (e.g., "basil")
   * @param limit - Maximum number of recipes to return
   * @returns Array of recipes containing the ingredient
   */
  getRecipesByIngredient(ingredient: string, limit?: number): Recipe[] {
    const ingredientLower = ingredient.toLowerCase();
    const filtered = this.recipes.filter((recipe) => {
      // Check primaryProduce array
      if (recipe.primaryProduce && recipe.primaryProduce.length > 0) {
        return recipe.primaryProduce.some((produce) =>
          produce.toLowerCase().includes(ingredientLower) ||
          ingredientLower.includes(produce.toLowerCase())
        );
      }

      // Fallback: check ingredients array if primaryProduce is empty
      if (recipe.ingredients && recipe.ingredients.length > 0) {
        return recipe.ingredients.some((ing) => {
          const ingStr = typeof ing === 'string' ? ing : String(ing);
          return ingStr.toLowerCase().includes(ingredientLower);
        });
      }

      return false;
    });

    return limit ? filtered.slice(0, limit) : filtered;
  }

  /**
   * Get recipes that use products from a specific farm
   * 🌟 CRITICAL for Farm → Recipe bidirectional linking
   * @param farmProducts - Array of product names the farm sells
   * @param limit - Maximum number of recipes to return
   * @returns Array of recipes using the farm's products
   */
  getRecipesForFarm(farmProducts: string[], limit?: number): Recipe[] {
    if (!farmProducts || farmProducts.length === 0) {
      return [];
    }

    const productsLower = farmProducts.map((p) => p.toLowerCase());
    const scored = this.recipes.map((recipe) => {
      let matchCount = 0;

      if (recipe.primaryProduce && recipe.primaryProduce.length > 0) {
        matchCount = recipe.primaryProduce.filter((produce) => {
          const produceLower = produce.toLowerCase();
          return productsLower.some(
            (farmProduct) =>
              produceLower.includes(farmProduct) ||
              farmProduct.includes(produceLower)
          );
        }).length;
      }

      return { recipe, matchCount };
    });

    const filtered = scored
      .filter((item) => item.matchCount > 0)
      .sort((a, b) => b.matchCount - a.matchCount)
      .map((item) => item.recipe);

    return limit ? filtered.slice(0, limit) : filtered;
  }

  /**
   * Get recipes by diet/dietary restriction
   * @param diet - Dietary tag (e.g., "vegan", "gluten-free", "vegetarian")
   * @param limit - Maximum number of recipes to return
   */
  getRecipesByDiet(diet: string, limit?: number): Recipe[] {
    const dietLower = diet.toLowerCase();
    const filtered = this.recipes.filter((recipe) =>
      recipe.dietaryTags && recipe.dietaryTags.some((tag) =>
        tag.toLowerCase().includes(dietLower)
      )
    );
    return limit ? filtered.slice(0, limit) : filtered;
  }
}

export default new RecipeAgent();
